import { Loader2 } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn.js';
import { EASE_OUT_QUINT } from '../motion.js';

export type ButtonVariant =
  | 'primary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'secondary'
  | 'default'
  /* Nomes antigos, mantidos para as telas. */
  | 'ghost'
  | 'surface'
  | 'ink'
  | 'danger-soft';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

/**
 * Botão do sistema (design.md §8): a faixa do trilho, de baixo para cima.
 *
 * O corpo já nasce tingido na cor da variante, a 12%, quadrado, com o texto
 * na mesma cor e uma **barra de 2px no pé**, a meia opacidade. Sob o ponteiro
 * (e sob o foco do teclado) o tingimento **sobe para 22%** — cheio, até o
 * topo, sem degradê. No clique, a coreografia do item do menu, virada: a
 * barra cheia **cresce do meio para as duas pontas** em 280ms e, completa,
 * uma faixa mais forte **varre de baixo para cima** em 400ms. É o único
 * movimento do botão.
 *
 * A cor é a função: laranja para a ação principal, verde para concluir,
 * vermelho para remover, âmbar para o que pede cuidado, azul para o neutro
 * informativo, tinta para o padrão e cinza para o discreto.
 */
interface Look {
  readonly text: string;
  readonly bar: string;
  /** Tingimento de repouso e o de hover, só quando habilitado. */
  readonly fill: string;
  /** A faixa que varre no clique. */
  readonly band: string;
}

const LOOKS: Record<ButtonVariant, Look> = {
  primary: {
    text: 'text-primary-ink',
    bar: 'bg-primary',
    fill: 'bg-primary/12 enabled:hover:bg-primary/22 enabled:focus-visible:bg-primary/22',
    band: 'bg-primary/25',
  },
  success: {
    text: 'text-success',
    bar: 'bg-success',
    fill: 'bg-success/12 enabled:hover:bg-success/22 enabled:focus-visible:bg-success/22',
    band: 'bg-success/25',
  },
  danger: {
    text: 'text-danger',
    bar: 'bg-danger',
    fill: 'bg-danger/12 enabled:hover:bg-danger/22 enabled:focus-visible:bg-danger/22',
    band: 'bg-danger/25',
  },
  warning: {
    text: 'text-warning',
    bar: 'bg-warning',
    fill: 'bg-warning/12 enabled:hover:bg-warning/22 enabled:focus-visible:bg-warning/22',
    band: 'bg-warning/25',
  },
  info: {
    text: 'text-info',
    bar: 'bg-info',
    fill: 'bg-info/12 enabled:hover:bg-info/22 enabled:focus-visible:bg-info/22',
    band: 'bg-info/25',
  },
  /* O discreto: Cancelar, Voltar, Fechar, a página que não é a atual. */
  secondary: {
    text: 'text-ink-2',
    bar: 'bg-ink-3',
    fill: 'bg-ink/6 enabled:hover:bg-ink/12 enabled:focus-visible:bg-ink/12',
    band: 'bg-ink/14',
  },
  default: {
    text: 'text-ink',
    bar: 'bg-ink',
    fill: 'bg-ink/8 enabled:hover:bg-ink/14 enabled:focus-visible:bg-ink/14',
    band: 'bg-ink/16',
  },
  ghost: {
    text: 'text-ink-2',
    bar: 'bg-ink-3',
    fill: 'bg-ink/6 enabled:hover:bg-ink/12 enabled:focus-visible:bg-ink/12',
    band: 'bg-ink/14',
  },
  surface: {
    text: 'text-ink',
    bar: 'bg-ink',
    fill: 'bg-ink/8 enabled:hover:bg-ink/14 enabled:focus-visible:bg-ink/14',
    band: 'bg-ink/16',
  },
  ink: {
    text: 'text-ink',
    bar: 'bg-ink',
    fill: 'bg-ink/8 enabled:hover:bg-ink/14 enabled:focus-visible:bg-ink/14',
    band: 'bg-ink/16',
  },
  'danger-soft': {
    text: 'text-danger',
    bar: 'bg-danger',
    fill: 'bg-danger/12 enabled:hover:bg-danger/22 enabled:focus-visible:bg-danger/22',
    band: 'bg-danger/25',
  },
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-3 text-[12px]',
  md: 'h-10 gap-2 px-4 text-[13px]',
  lg: 'h-11 gap-2 px-6 text-sm',
  icon: 'size-10 shrink-0',
};

/** A barra do pé, e a faixa que sobe dela no clique. */
const BAR = 'pointer-events-none absolute inset-x-0 bottom-0 h-0.5';
const BAND = 'pointer-events-none absolute inset-0';

/** Quanto dura a coreografia do clique: a barra, depois a faixa. */
const GROW_S = 0.28;
const SWEEP_S = 0.4;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly loading?: boolean;
  readonly icon?: ReactNode;
}

export function Button({
  variant = 'default',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  onPointerDown,
  ...rest
}: ButtonProps) {
  const [pulse, setPulse] = useState(0);
  const look = LOOKS[variant];
  const inactive = disabled === true || loading;

  // A coreografia do clique sai do DOM quando termina.
  useEffect(() => {
    if (pulse === 0) return undefined;
    const timer = window.setTimeout(() => setPulse(0), (GROW_S + SWEEP_S) * 1000 + 100);
    return () => window.clearTimeout(timer);
  }, [pulse]);

  return (
    <button
      type={type}
      // Largura não muda ao entrar em carregamento: o spinner ocupa o lugar do ícone.
      disabled={inactive}
      aria-busy={loading}
      onPointerDown={(event) => {
        if (!inactive) setPulse((value) => value + 1);
        onPointerDown?.(event);
      }}
      className={cn(
        'relative inline-flex items-center justify-center overflow-hidden font-medium whitespace-nowrap',
        'transition-[background-color,color,transform] duration-200 ease-out',
        // Resposta ao toque: encolhe um fio enquanto pressionado. Desabilitado não responde.
        'active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 disabled:active:scale-100',
        look.fill,
        look.text,
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {/* A barra de repouso, a meia opacidade. */}
      <span aria-hidden className={cn(BAR, look.bar, 'opacity-45')} />

      {pulse > 0 ? (
        <span key={pulse} aria-hidden className="contents">
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1, transition: { duration: GROW_S, ease: EASE_OUT_QUINT } }}
            style={{ transformOrigin: 'center' }}
            className={cn(BAR, look.bar)}
          />
          <motion.span
            initial={{ scaleY: 0 }}
            animate={{
              scaleY: 1,
              transition: { delay: GROW_S, duration: SWEEP_S, ease: EASE_OUT_QUINT },
            }}
            style={{ transformOrigin: 'bottom center' }}
            className={cn(BAND, look.band)}
          />
        </span>
      ) : null}

      <span className="relative z-10 inline-flex items-center gap-[inherit]">
        {loading ? (
          <Loader2 size={15} strokeWidth={2} className="animate-spin" aria-hidden />
        ) : (
          icon
        )}
        {children}
      </span>
    </button>
  );
}
